import { useThemeScope } from "./theme/theme";
import "./apps/view-link-session";
import { gardenSurface } from "./mobile/surface";
import { workspaceViewPath } from "@knowledge/pages/urls";
import { ErrorBoundary } from "./errors/ErrorBoundary";
import { ErrorToasts } from "./errors/ErrorToasts";
import { lazy, StrictMode, Suspense, useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import "@fontsource-variable/dm-sans";
import "@fontsource/dm-mono/400.css";
import { GardenPage } from "./garden/GardenPage";
import { GardenLoading } from "./garden/GardenLoading";
import { initialSession } from "./sync/login-link";
import "./analytics/posthog";

const PublicReader = lazy(() => import("./reader/PublicReader"));
const WorkspaceApp = lazy(() => import("./WorkspaceApp"));
if (
  import.meta.env.VITE_SYNC_MODE === "remote" &&
  (location.pathname !== "/" || location.hash || location.search)
)
  void initialSession().catch(() => {});

function Root() {
  const [url, setUrl] = useState(() => new URL(location.href));
  useEffect(() => {
    const navigate = () => setUrl(new URL(location.href));
    window.addEventListener("popstate", navigate);
    window.addEventListener("hashchange", navigate);
    return () => {
      window.removeEventListener("popstate", navigate);
      window.removeEventListener("hashchange", navigate);
    };
  }, []);
  const directPage = workspaceViewPath(url.pathname).match(
    /^\/(s|p)\/[^/]*--([a-zA-Z0-9_-]{1,64})$/,
  );
  // Only gate a direct workspace-page arrival. Once the workspace is mounted,
  // it owns authentication; gating later navigation would discard its drafts.
  const [anonymous, setAnonymous] = useState<boolean | null>(() =>
    directPage?.[1] === "p" && import.meta.env.VITE_SYNC_MODE === "remote"
      ? null
      : false,
  );
  useEffect(() => {
    if (anonymous !== null) return;
    let active = true;
    void initialSession()
      .then(() => {
        if (active) setAnonymous(false);
      })
      .catch(() => {
        if (active) setAnonymous(true);
      });
    return () => {
      active = false;
    };
  }, [anonymous]);
  const legacyNotebook =
    new URLSearchParams(url.hash.slice(1)).has("page") ||
    url.searchParams.has("lab") ||
    url.searchParams.has("__verification");
  const landing = url.pathname === "/" && !legacyNotebook;
  const publicReader =
    !!directPage &&
    import.meta.env.VITE_SYNC_MODE === "remote" &&
    (directPage[1] === "s" || anonymous === true || !!gardenSurface(url.search));
  const viewPath = workspaceViewPath(url.pathname);
  useThemeScope(
    landing || publicReader || /^\/(v|present)(\/|$)/.test(viewPath) ||
    viewPath === "/ios" || gardenSurface(url.search)
      ? "light"
      : "workspace",
  );
  if (directPage && import.meta.env.VITE_SYNC_MODE === "remote") {
    if (directPage[1] === "p" && anonymous === null) return <GardenLoading />;
    if (directPage[1] === "s" || anonymous || gardenSurface(url.search))
      return (
        <Suspense fallback={<GardenLoading />}>
          <PublicReader key={directPage[2]} pageId={directPage[2]} />
        </Suspense>
      );
  }
  if (landing) return <GardenPage />;
  return (
    <Suspense fallback={<GardenLoading />}>
      <WorkspaceApp />
    </Suspense>
  );
}

const root = document.getElementById("root");
if (!root) throw new Error("Missing application root");
createRoot(root).render(
  <StrictMode>
    <ErrorToasts />
    <ErrorBoundary name="Workspace">
      <Root />
    </ErrorBoundary>
  </StrictMode>,
);
